import {
  ref,
  onMounted,
  defineComponent,
  computed,
} from "@vue/composition-api";
import FormErrors from "@/components/FormErrors/FormErrors.vue";
import LoadingAnimation from "@/components/LoadingAnimation/LoadingAnimation.vue";
import { v4 as uuidv4 } from "uuid";
import { JSConn } from "@/utilities/js-conn";

import { TOAST_OPTIONS } from "@/constants";
import { NamedRts } from "@/router";
import { buildSubmitButton, buildFormErrors } from "@/utilities/form-helpers";

import {
  buildFormLineItem,
  prettifyLineItemStatus,
  prettifyLineItemCategory,
} from "@/models/LineItem";
import { LineItem } from "@/store/modules/lineItems";

export default defineComponent({
  name: "lineItemEditForm",
  components: {
    FormErrors,
    LoadingAnimation,
  },
  props: {
    lineItemId: {
      type: String,
      required: true,
    },
  },

  setup(props, ctx) {
    let $store = ctx.root.$store;

    let dataLoaded = ref(false);
    let loadingMessage = ref("Preparing the form...");

    let firstInput: any = ref(null);
    let deleteModal: any = ref(null);

    const showDeleteModal = () => {
      deleteModal.value.show();
    };

    const hideDeleteModal = () => {
      deleteModal.value.hide();
    };

    const deleting = ref(false);

    const deleteLineItem = () => {
      deleting.value = true;
      const jsConn = new JSConn();
      jsConn.formParams = {
        id: props.lineItemId,
      };
      $store.dispatch("deleteLineItem", jsConn).then(jsConn => {
        deleting.value = false;
        ctx.root.$router.push(cancelParams());
        return Promise.resolve(jsConn);
      });
    };

    const cancelParams = () => {
      if (props.lineItemId === "new") {
        return {
          name: NamedRts.lineItems,
        };
      } else {
        return {
          name: NamedRts.lineItems,
        };
      }
    };

    let {
      loadLineItemIntoForm,
      formLineItem,
      lineItemStatuses,
      loadLineItemStatusesIntoForm,
      lineItemCategories,
      loadLineItemCategoriesIntoForm,
    } = buildFormLineItem();

    const apiLineItem = computed(() => {
      return $store.getters.lineItem as LineItem;
    });

    onMounted(() => {
      let dataPromises = [];
      let jsConn = new JSConn();
      loadLineItemStatusesIntoForm(lineItemStatuses);
      loadLineItemCategoriesIntoForm(lineItemCategories);

      if (props.lineItemId !== "new") {
        jsConn.formParams = {
          id: props.lineItemId,
        };

        dataPromises.push($store.dispatch("needLineItem", jsConn));
      }

      Promise.all(dataPromises)
        .then(() => {
          if (props.lineItemId !== "new") {
            loadLineItemIntoForm(apiLineItem.value, formLineItem);
          }
        })
        .finally(() => {
          dataLoaded.value = true;
          ctx.root.$nextTick(() => {
            if (firstInput.value) {
              firstInput.value.focus();
            }
          });
        });
    });

    let { submitButtonText, submitting } = buildSubmitButton();
    let { hasFormErrors, formErrors } = buildFormErrors();

    const onSubmit = (evt: any) => {
      evt.preventDefault();
      const jsConn = new JSConn();

      hasFormErrors.value = false;
      formErrors.value = [];
      submitting.value = true;
      submitButtonText.value = "Submitting...";

      let storeDispatch;

      if (props.lineItemId === "new") {
        formLineItem.id = uuidv4();
        jsConn.formParams = formLineItem;
        storeDispatch = "createLineItem";
      } else {
        jsConn.formParams = formLineItem;
        storeDispatch = "updateLineItem";
      }

      $store
        .dispatch(storeDispatch, jsConn)
        .then(jsConn => {
          ctx.root.$bvToast.toast(
            "Line item was successfully saved",
            TOAST_OPTIONS,
          );
          ctx.root.$router.push(cancelParams());
          return Promise.resolve(jsConn);
        })
        .catch(jsConn => {
          hasFormErrors.value = true;
          formErrors.value = jsConn.error["response"]["data"];
        });
    };

    return {
      dataLoaded,
      loadingMessage,
      firstInput,
      cancelParams,

      formLineItem,

      onSubmit,
      submitButtonText,
      submitting,
      hasFormErrors,
      formErrors,

      lineItemStatuses,
      prettifyLineItemStatus,

      lineItemCategories,
      prettifyLineItemCategory,

      deleteModal,
      showDeleteModal,
      hideDeleteModal,
      deleting,
      deleteLineItem,
    };
  },
});
